import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { Search, Sparkles, TrendingUp, TrendingDown, Minus } from "lucide-react";

const SENTIMENT_STYLES = {
  very_bearish: { bg: "rgba(239,68,68,0.18)", border: "rgba(239,68,68,0.45)", color: "#fecaca", Icon: TrendingDown, label: "very bearish" },
  bearish:      { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.30)", color: "#fca5a5", Icon: TrendingDown, label: "bearish" },
  neutral:      { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.10)", color: "#a3a3a3", Icon: Minus, label: "neutral" },
  bullish:      { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)", color: "#86efac", Icon: TrendingUp, label: "bullish" },
  very_bullish: { bg: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.45)", color: "#bbf7d0", Icon: TrendingUp, label: "very bullish" },
};

const SentimentChip = ({ s }) => {
  const sty = SENTIMENT_STYLES[s.label] || SENTIMENT_STYLES.neutral;
  const Icon = sty.Icon;
  return (
    <div
      data-testid={`sentiment-${s.label}`}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-mono-num"
      style={{ background: sty.bg, border: `1px solid ${sty.border}`, color: sty.color }}
    >
      <Icon size={10} />
      <span>{sty.label}</span>
      <span className="opacity-70">{s.score > 0 ? `+${s.score.toFixed(2)}` : s.score.toFixed(2)}</span>
    </div>
  );
};

const News = () => {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("business");
  const [loading, setLoading] = useState(true);
  const [sentimentOn, setSentimentOn] = useState(true);
  const [sortBy, setSortBy] = useState("time"); // "time" | "bull" | "bear"

  const load = (query, withSent = sentimentOn) => {
    setLoading(true);
    const base = query ? `/news?q=${encodeURIComponent(query)}` : `/news?category=${filter}`;
    const url = `${base}${withSent ? "&with_sentiment=true" : ""}`;
    client.get(url).then(({ data }) => {
      setArticles(data.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(""); }, [filter, sentimentOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => { e.preventDefault(); load(q); };

  const sortedArticles = (() => {
    if (sortBy === "bull") return [...articles].sort((a, b) => (b.sentiment?.score ?? -2) - (a.sentiment?.score ?? -2));
    if (sortBy === "bear") return [...articles].sort((a, b) => (a.sentiment?.score ?? 2) - (b.sentiment?.score ?? 2));
    return articles;
  })();

  const sentimentStats = (() => {
    const scored = articles.filter((a) => a.sentiment);
    if (!scored.length) return null;
    const avg = scored.reduce((s, a) => s + a.sentiment.score, 0) / scored.length;
    const bull = scored.filter((a) => a.sentiment.score > 0.1).length;
    const bear = scored.filter((a) => a.sentiment.score < -0.1).length;
    return { avg, bull, bear, total: scored.length };
  })();

  return (
    <div className="space-y-8 fade-in-up" data-testid="news-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <span className="chip">News Stream</span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Live Market News</h1>
          <p className="text-neutral-400 mt-2 text-sm">Filter by asset name or ticker, e.g. "Tesla" or "Bitcoin". AI sentiment scores each article.</p>
        </div>
        <form onSubmit={submit} className="relative w-full lg:w-96">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            data-testid="news-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search asset news…"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-white/20"
          />
        </form>
      </div>

      {/* Sentiment summary */}
      {sentimentStats && (
        <div className="glass p-5 flex flex-wrap items-center justify-between gap-4" data-testid="sentiment-summary">
          <div className="flex items-center gap-3">
            <Sparkles size={14} className="text-blue-400" />
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Aggregate Sentiment</div>
              <div className="font-display text-xl mt-0.5 flex items-center gap-2">
                <span className={sentimentStats.avg >= 0 ? "up" : "down"}>{sentimentStats.avg >= 0 ? "+" : ""}{sentimentStats.avg.toFixed(2)}</span>
                <span className="text-xs text-neutral-500 font-mono-num">avg score / {sentimentStats.total} articles</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full" style={{ background: "rgba(16,185,129,0.15)", color: "#86efac" }}>
              <TrendingUp size={11} /> {sentimentStats.bull} bullish
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full" style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5" }}>
              <TrendingDown size={11} /> {sentimentStats.bear} bearish
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 text-neutral-300">
              <Minus size={11} /> {sentimentStats.total - sentimentStats.bull - sentimentStats.bear} neutral
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2 flex-wrap">
          {["business", "technology", "general"].map((c) => (
            <button
              key={c}
              data-testid={`news-filter-${c}`}
              onClick={() => { setQ(""); setFilter(c); }}
              className={`chip ${filter === c && !q ? "!bg-white !text-black !border-white" : ""}`}
            >
              {c}
            </button>
          ))}
          {["Bitcoin", "Ethereum", "Tesla", "NVIDIA", "Apple"].map((k) => (
            <button
              key={k}
              data-testid={`news-quick-${k}`}
              onClick={() => { setQ(k); load(k); }}
              className="chip hover:!bg-white/10"
            >
              #{k}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="sentiment-toggle"
            onClick={() => setSentimentOn((v) => !v)}
            className={`chip ${sentimentOn ? "!bg-blue-500/20 !border-blue-500/40 !text-blue-300" : ""} inline-flex items-center gap-1`}
          >
            <Sparkles size={11} /> AI Sentiment {sentimentOn ? "ON" : "OFF"}
          </button>
          {sentimentOn && (
            <>
              <button data-testid="sort-time" onClick={() => setSortBy("time")} className={`chip ${sortBy === "time" ? "!bg-white !text-black" : ""}`}>Latest</button>
              <button data-testid="sort-bull" onClick={() => setSortBy("bull")} className={`chip ${sortBy === "bull" ? "!bg-emerald-500/20 !border-emerald-500/40 !text-emerald-300" : ""}`}>Most Bullish</button>
              <button data-testid="sort-bear" onClick={() => setSortBy("bear")} className={`chip ${sortBy === "bear" ? "!bg-rose-500/20 !border-rose-500/40 !text-rose-300" : ""}`}>Most Bearish</button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && Array.from({ length: 6 }).map((_, i) => (
          <div key={`sk-${i}`} className="glass p-6 animate-pulse h-48" />
        ))}
        {!loading && sortedArticles.map((a) => {
          const s = a.sentiment;
          const accent = s
            ? (SENTIMENT_STYLES[s.label] || SENTIMENT_STYLES.neutral)
            : null;
          return (
            <a
              key={a.url}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`news-card-${a.url}`}
              className="glass glass-hover overflow-hidden block transition-all"
              style={accent ? { borderColor: accent.border, boxShadow: `0 0 0 1px ${accent.border}` } : {}}
            >
              {a.image && (
                <img src={a.image} alt="" className="w-full h-44 object-cover" onError={(e) => (e.target.style.display = "none")} />
              )}
              <div className="p-5">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 truncate">{a.source}</div>
                  {s && <SentimentChip s={s} />}
                </div>
                <div className="font-display text-lg leading-snug line-clamp-3">{a.title}</div>
                {a.description && <p className="text-sm text-neutral-400 mt-2 line-clamp-3">{a.description}</p>}
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-neutral-500">{a.published_at ? new Date(a.published_at).toLocaleString() : ""}</div>
                  {s?.tag && (
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 bg-white/[0.03] border border-white/10 px-2 py-0.5 rounded-full">
                      {s.tag}
                    </span>
                  )}
                </div>
              </div>
            </a>
          );
        })}
        {!loading && sortedArticles.length === 0 && <div className="text-neutral-500 col-span-3 text-center py-12">No articles. Try a different search.</div>}
      </div>
    </div>
  );
};

export default News;
